import React from 'react'
import styled from 'styled-components'
import { marginSizes, paddingSizes } from '@jarvis.oddle/core/theme/default'
import { getEmailError } from '@jarvis.oddle/core/tools/validation'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Divider from '@jarvis.oddle/core/components/Divider'
import Modal, {
  CloseButton,
  PrevButton
} from '@jarvis.oddle/core/components/Modal'
import IconOddlePass from '@jarvis.oddle/core/components/Icons/IconOddlePass'
import Button from '@jarvis.oddle/core/components/Button'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import PoweredByOddlePass, {
  UTMSource
} from 'src/components/molecules/PoweredByOddlePass'
import loginFlow from 'src/flows/loginFlow'
import FacebookLogin from 'src/components/molecules/FacebookLogin'
import UserContext from 'src/containers/UserContext'
import { isObjectEmpty } from 'src/utils/object'
import { checkUserEmailAction } from 'src/redux/user'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import useTracker from 'src/hooks/useTracker'
import { makeControlledModal } from 'src/decorators/ModalController'

const StyledFlex = styled(Flex).attrs(() => ({
  alignItems: 'center',
  marginTop: marginSizes.small,
  marginBottom: marginSizes.medium
}))``

const SubmitButton = React.memo(
  ({
    disabled,
    loading,
    onClick,
    ...props
  }: {
    disabled: boolean
    loading: boolean
    onClick: (params) => void
    [props: string]: unknown
  }) => {
    const { values, setFieldError, validateForm } = useFormContext()

    const onButtonClick = React.useCallback(async () => {
      await onClick({ values, setFieldError, validateForm })
    }, [onClick, setFieldError, validateForm, values])

    return (
      <Button
        disabled={disabled}
        loading={loading}
        onClick={onButtonClick}
        {...props}
      />
    )
  }
)

const AccountChooserModal = React.memo(
  ({
    modal,
    email = ''
  }: {
    modal: {
      show: boolean
      onClose: () => void
      onClosed: () => void
    }
    email: string
  }) => {
    const { t } = React.useContext(TranslationContext)
    const dispatch = useAppDispatch()
    const meta = useAppSelector(selectDetailMenuSite)
    const [signingIn, setSigningIn] = React.useState(false)
    const [signingUp, setSigningUp] = React.useState(false)
    const {
      facebookSignin,
      facebookRedirectUrl,
      storeOrderId,
      menuId,
      facebookAppId
    } = meta || {}
    const { isOddlePassEnabled } = React.useContext(UserContext)
    const { notifyAccountLead } = useTracker()

    const handleSubmit = React.useCallback(
      async ({ values, setFieldError, setSubmitting, validateForm }) => {
        const error = await validateForm()
        if (!isObjectEmpty(error)) {
          return
        }

        const { email } = values
        setSubmitting(true)
        const response = await dispatch(
          checkUserEmailAction({ userEmail: email, source: null })
        )
        setSubmitting(false)
        if (response.meta.requestStatus === 'fulfilled') {
          notifyAccountLead()
          const payload = response.payload
          if (payload.mcaExist) {
            // User already have account for this menu.
            loginFlow.send('logIn', { email, mcaExist: true })
          } else {
            if (payload.ocaExist) {
              // User doesn't have account for this menu but have OddlePass account.
              loginFlow.send('logIn', { email, mcaExist: false })
            } else {
              // User doesn't existed in system.
              loginFlow.send('signUp', { email })
            }
          }
        } else {
          setFieldError('general', response.payload.error)
        }
      },
      [dispatch, notifyAccountLead]
    )

    const handleValidate = React.useCallback(
      ({ email }) => {
        const errors = {} as {
          email: string
        }

        const emailError = getEmailError(t, email)
        if (emailError) errors.email = emailError

        return errors
      },
      [t]
    )

    const onLoginClick = React.useCallback(
      async ({ values, setFieldError, validateForm }) => {
        await handleSubmit({
          values,
          setFieldError,
          validateForm,
          setSubmitting: setSigningIn
        })
      },
      [handleSubmit]
    )

    const onSignUpClick = React.useCallback(
      async ({ values, setFieldError, validateForm }) => {
        await handleSubmit({
          values,
          setFieldError,
          validateForm,
          setSubmitting: setSigningUp
        })
      },
      [handleSubmit]
    )

    return (
      <Modal {...modal} slideInAnimation='top'>
        <Modal.FlexContainer>
          <Modal.Header>
            {loginFlow.getMeta().onExit && (
              <PrevButton onClick={() => loginFlow.exit()} />
            )}
            {isOddlePassEnabled ? (
              <IconOddlePass />
            ) : (
              <H4 bold normalised>
                {t("Let's get started")}
              </H4>
            )}
            <CloseButton onClick={modal.onClose} />
          </Modal.Header>
          <Modal.Body withoutFooter>
            <Modal.Section flexGrow={1}>
              <H4 bold normalised>
                {t('Enter you email to begin.')}
              </H4>
              <Form initialValues={{ email }} onValidate={handleValidate}>
                <Form.ErrorMessage>
                  {({ errors }) => (errors.general ? t(errors.general) : null)}
                </Form.ErrorMessage>
                <Div paddingTop={paddingSizes.small}>
                  <Form.Input
                    type='email'
                    name='email'
                    label={t('Email Address')}
                  />
                </Div>
                <SubmitButton
                  fullWidth
                  key='signin'
                  loading={signingIn}
                  disabled={signingIn || signingUp}
                  onClick={onLoginClick}
                >
                  {t('Log In')}
                </SubmitButton>
                <Div width='100%' marginTop={marginSizes.small}>
                  <SubmitButton
                    fullWidth
                    secondary
                    key='signup'
                    loading={signingUp}
                    disabled={signingIn || signingUp}
                    onClick={onSignUpClick}
                  >
                    {t('Sign Up')}
                  </SubmitButton>
                </Div>
              </Form>
              {facebookSignin && facebookRedirectUrl ? (
                <Div paddingTop={paddingSizes.small}>
                  <StyledFlex>
                    <Divider />
                    <H4
                      normailised
                      paddingLeft={paddingSizes.large}
                      paddingRight={paddingSizes.large}
                    >
                      {t('Or')}
                    </H4>
                    <Divider />
                  </StyledFlex>
                  <FacebookLogin
                    facebookRedirectUrl={facebookRedirectUrl}
                    facebookAppId={facebookAppId}
                    storeOrderId={storeOrderId}
                    menuId={menuId}
                  />
                </Div>
              ) : null}
              <PoweredByOddlePass
                UTMSource={UTMSource.SIGNIN_MODAL}
                copyText={t(
                  "If you've ordered from other restaurant partners on Oddle, you can use that email again."
                )}
              />
            </Modal.Section>
          </Modal.Body>
        </Modal.FlexContainer>
      </Modal>
    )
  }
)

export default makeControlledModal(AccountChooserModal, 'AccountChooserModal')
